/**
 * PresetProvider
 *
 * - Owns the effective preset for the current client session
 * - Fetches GET /api/appearance/me (guest-safe)
 * - Applies theme CSS variables
 * - Exposes { preset, isLoading, source, refresh, setPreviewPreset }
 *
 * First paint: uses a cached preset from localStorage OR the hardcoded
 * FALLBACK_PRESET so the UI never shows unstyled content.
 */
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from "react";

import { FALLBACK_PRESET } from "../registry";
import { applyTheme, ensureDefaultTheme } from "../themes/applyTheme";
import {
    fetchEffectivePreset,
    PRESET_CACHE_KEY,
} from "@/services/appearanceService";
import { useApp } from "@/context/AppContext";

const PresetContext = createContext(null);

function _readCache() {
    try {
        const raw = localStorage.getItem(PRESET_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.preset ? parsed : null;
    } catch {
        return null;
    }
}

function _writeCache(payload) {
    try {
        localStorage.setItem(PRESET_CACHE_KEY, JSON.stringify(payload));
    } catch {
        /* storage unavailable — ignore */
    }
}

export function PresetProvider({ children }) {
    const { isLoggedIn } = useApp();

    // Use the cached preset if available, else the hardcoded fallback, so the
    // first render is always something sensible (and Preset 1-identical in
    // worst case).
    const [state, setState] = useState(() => {
        const cached = _readCache();
        return (
            cached || {
                effective_preset_id: FALLBACK_PRESET.id,
                source: "fallback",
                preset: FALLBACK_PRESET,
            }
        );
    });
    const [isLoading, setIsLoading] = useState(true);
    const [previewPreset, setPreviewPresetState] = useState(null); // admin preview only

    // Guard against setState after unmount. Must RESET to true on (re)mount
    // because React StrictMode will mount -> unmount -> remount the same
    // component and the ref object survives across all three phases.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Apply theme immediately on mount AND whenever preset changes.
    useEffect(() => {
        ensureDefaultTheme();
    }, []);

    const activePreset = previewPreset || state.preset || FALLBACK_PRESET;
    useEffect(() => {
        applyTheme(activePreset.theme_id, activePreset.id);
    }, [activePreset.theme_id, activePreset.id]);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchEffectivePreset();
            if (!mountedRef.current) return;
            if (data && data.preset) {
                setState(data);
                _writeCache(data);
            }
        } catch (err) {
            // Silent: keep cached/fallback preset
            // Backend will be retried on next login/logout
            // eslint-disable-next-line no-console
            console.warn("[PresetProvider] fetchEffectivePreset failed:", err);
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Re-fetch on login/logout so per-user overrides are honored immediately.
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const setPreviewPreset = useCallback((preset) => {
        setPreviewPresetState(preset || null);
    }, []);

    const value = useMemo(
        () => ({
            preset: activePreset,
            effectivePresetId: activePreset.id,
            source: previewPreset ? "preview" : state.source,
            isLoading,
            refresh,
            setPreviewPreset,
            hasPreview: !!previewPreset,
        }),
        [activePreset, state.source, isLoading, refresh, previewPreset, setPreviewPreset],
    );

    return <PresetContext.Provider value={value}>{children}</PresetContext.Provider>;
}

export function usePreset() {
    const ctx = useContext(PresetContext);
    if (!ctx) {
        // Allow usage outside provider without throwing — return a safe default.
        return {
            preset: FALLBACK_PRESET,
            effectivePresetId: FALLBACK_PRESET.id,
            source: "fallback",
            isLoading: false,
            refresh: async () => {},
            setPreviewPreset: () => {},
            hasPreview: false,
        };
    }
    return ctx;
}

export default PresetProvider;
